import { useRef, useState } from "react";
import { useLantern, useSession } from "../hooks";
import { ApiError } from "../sync/api";
import type { ModalView } from "../sync/types";
import { BlockList, type Block, type InputValue } from "./Blocks";
import { t } from "../i18n";
import { Form, useDialog } from "./ui";

// Modal shows the top view an app opened (Slack's views.open): its blocks,
// with input blocks as a form. Submitting sends the values to the app, whose
// answer closes, updates or pushes the view, or shows errors.
export function Modal() {
  const state = useLantern();
  const top = state.modals.at(-1);
  // A new view starts with fresh values.
  return top ? <ModalBody key={top.id} view={top} /> : null;
}

function ModalBody({ view }: { view: ModalView }) {
  const session = useSession();
  const [values, setValues] = useState<Record<string, Record<string, InputValue>>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const ref = useRef<HTMLFormElement>(null);
  const pop = (all: boolean) =>
    session.store.update((s) => {
      s.modals = all ? [] : s.modals.filter((m) => m.id !== view.id);
    });

  const submit = async () => {
    try {
      const r = await session.api.submitView(view.id, values);
      if (r.action === "errors") setErrors(r.errors ?? {});
      else if (r.action === "close") pop(false);
      else if (r.action === "clear") pop(true);
      else if (r.view) {
        const v = r.view;
        session.store.update((s) => {
          s.modals = r.action === "push" ? [...s.modals, v] : s.modals.map((m) => (m.id === view.id ? v : m));
        });
      }
    } catch (err) {
      setErrors({ "": err instanceof ApiError ? err.message : String(err) });
    }
  };
  const close = async () => {
    pop(!!view.clear_on_close);
    await session.api.closeView(view.id).catch(() => {});
  };

  useDialog(ref, () => void close());

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && void close()}>
      <Form
        ref={ref}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={view.title.text}
        tabIndex={-1}
        onSubmit={submit}
      >
        <h2>{view.title.text}</h2>
        <BlockList
          blocks={view.blocks as Block[]}
          onAction={(blockID, actionID, value) => session.api.viewAction(view.id, { block_id: blockID, action_id: actionID, value }, values)}
          inputs={{
            values,
            errors,
            set: (b, a, v) => setValues((old) => ({ ...old, [b]: { ...old[b], [a]: v } })),
          }}
        />
        {errors[""] && <p className="error">{errors[""]}</p>}
        <div className="modal-buttons">
          <button type="button" onClick={() => void close()}>
            {view.close?.text ?? t("Cancel")}
          </button>
          {view.submit && (
            <button type="submit" className="primary">
              {view.submit.text}
            </button>
          )}
        </div>
      </Form>
    </div>
  );
}
